import {
    DisplayType, DisplayValue, HorizontalAlignment, MetadataField, ModelRow, StringUtil, VerticalAlignment
} from "@mcleod/core";
import { Component } from "../../base/Component";
import { ComponentTypes } from "../../base/ComponentTypes";
import { DataSource } from "../../databinding/DataSource";
import { DeserializeProps } from "../../serializer/ComponentDeserializer";
import { Panel } from "../panel/Panel";
import { PropType } from "../../base/PropType";
import { SpecialParentType } from "../../base/SpecialParentType";
import { Table } from "./Table";
import { TableCellPropDefinitions, TableCellProps } from "./TableCellProps";
import { TableCellStyles } from "./TableCellStyles";
import { TableColumn } from "./TableColumn";
import { TableRow } from "./TableRow";
import { TableRowMode } from "./TableRowMode";

export class TableCell extends Panel implements TableCellProps {
    public _table: Table;
    public _col: TableColumn;
    private _caption: string;
    public displayType: DisplayType;
    public format: string;
    private _row: TableRow;
    private _printableToggleEnabled: boolean;
    private hiddenComponents: Component[];

    constructor(props?: Partial<TableCellProps>) {
        super(props);
        this.defaultId();
    }

    isSerialized(): boolean {
        return true;
    }

    override doAfterDeserialize() {
        super.doAfterDeserialize();
        // Only deserialized panels are displayed to the user in the Permissions Manager, so only those panels need to
        // supports edit security.  This lets us avoid setting supportsEditSecurity = false in several places.
        // Specific to table cells: only normal table cells should support edit security.  Heading table cells and
        // select checkbox table cells should not support edit security.
        this.supportsEditSecurity = Object.getPrototypeOf(this) === TableCell.prototype;
    }

    override setProps(props: Partial<TableCellProps>) {
        super.setProps(props);
    }

    get table(): Table {
        return this._table;
    }

    set table(value: Table) {
        this._table = value;
        (this as any)._parent = this.table;
        this.table._applyCellProps(this);
    }

    get row(): TableRow {
        return this._row;
    }

    set row(value: TableRow) {
        this._row = value;
    }

    get col(): TableColumn {
        return this._col;
    }

    set col(value: TableColumn) {
        this._col = value;
        this.width = value?.headingCell?.width;
        this.minWidth = value?.headingCell?.minWidth;
        this.maxWidth = value?.headingCell?.maxWidth;
    }

    get isFixedWidth(): boolean {
        return this.width
            && this.minWidth
            && this.width == this.minWidth
            && this.minWidth === this.maxWidth;
    }

    set fixedWidth(value: string | number) {
        this.width = value;
        this.minWidth = value;
        this.maxWidth = value;
    }

    public get printableToggleEnabled(): boolean {
        return this._printableToggleEnabled == null ? true : this._printableToggleEnabled;
    }

    public set printableToggleEnabled(value: boolean) {
        this._printableToggleEnabled = value;
    }

    protected override addDefaultClassName() {
        this.element.classList.add(TableCellStyles.base);
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        super.displayComponentData(data, allData, rowIndex);

        //if field has been specified and no components are present, show value from field in cell's caption
        //this shouldn't happen anymore in non-designer screens due to populateSimpleComponent()
        if (this._designer == null && this.field != null && this.components.length == 0) {
            if (data == null)
                this.caption = null;
            else {
                if (data instanceof ModelRow) // this *should* always be true but isn't while PropertiesTable adds rows with raw data to the Tabel
                    this.caption = DisplayValue.getDisplayValue(data.get(this.field), this.displayType, this.format);
                else
                    this.caption = DisplayValue.getDisplayValue(data[this.field], this.displayType, this.format);
            }
        }
    }

    override set align(value: HorizontalAlignment) {
        super.align = value;
        if (value === HorizontalAlignment.RIGHT)
            this._element.style.textAlign = "right";
        else if (value === HorizontalAlignment.CENTER)
            this._element.style.textAlign = "center";
        else
            this._element.style.textAlign = "";
    }

    override get align(): HorizontalAlignment {
        return super.align;
    }

    get caption(): string {
        return this._caption;
    }

    set caption(value: string) {
        const oldValue = this._caption;
        this._caption = value;
        if (value == null)
            this._element.innerText = "";
        else
            this._element.innerText = value;
        this._matchIdToValue(oldValue, value);
    }

    get verticalAlign() {
        return super.verticalAlign;
    }

    set verticalAlign(value) {  // does this work without this getter/setter????  Panel does something similar.
        super.verticalAlign = value;
        if (value === VerticalAlignment.CENTER)
            this._element.style.verticalAlign = "middle";
        else if (value === VerticalAlignment.BOTTOM)
            this._element.style.verticalAlign = "bottom";
        else
            this._element.style.verticalAlign = "";
    }

    override getPropertyDefinitions() {
        return TableCellPropDefinitions.getDefinitions();
    }

    override async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        const compDef = props.def;
        //if cell doesn't define any components and a dataSource field is defined, create simple component in the component def
        //create textbox if cell is editable, label if not
        //(don't do this in the designer)
        if (this._designer == null) {
            const field = compDef.field;
            const dataSource = this.table.dataSource;
            this.populateSimpleComponent(compDef, field, dataSource);
        }

        const result = await super._deserializeSpecialProps({...props});
        if (this._designer == null) {
            // this code (and the related block below that is also commented out) was used to save changes on the blur of every component
            // let addlCompListeners = null;
            // if (this.table.rowModeControlType === RowModeControlType.AUTO) {
            //   addlCompListeners = {};
            //   addlCompListeners[ComponentListenerDefs.blur.domEventName] = {
            //     def: ComponentListenerDefs.blur,
            //     listeners: [
            //       (event: BlurEvent) => this.row.autoSave(event)
            //     ]
            //   }
            // }

            const boundRowPresent = this.row.boundRow != null;
            const inSearchMode = this.row.mode === TableRowMode.SEARCH;
            this.forEveryChildComponent(component => {
                if (component.boundRow == null && boundRowPresent)
                    component.boundRow = this.row.boundRow;
                // Need a better way to know a component allows indeterminate
                if (component.serializationName === "checkbox" || component.serializationName === "switch" ||
                    component.serializationName === "radioBox" || component.serializationName === "radioButtonGroup")
                    (component as any).allowIndeterminate = inSearchMode;
            });
            if (compDef.printableToggleEnabled && this.table.printableToggleEnabled) {
                const initiallyPrintable = this.row.rowIsPrintable();
                for (const component of this.components) {
                    if (component instanceof TableCell && !component.printableToggleEnabled)
                        continue;
                    if (this.row.mode !== TableRowMode.QUICK_ADD) {
                        if (component["printable"] != null)
                            component["printable"] = initiallyPrintable;
                        // if (addlCompListeners != null) {
                        //   component.addListenersToAll(addlCompListeners);
                        // }
                    }
                    else {
                        if (component["printable"] != null) {
                            component["printable"] = false;
                        }
                    }
                }
            }
        }
        return result;
    }

    populateSimpleComponent(compDef: any, field: string, dataSource: DataSource) {
        if (compDef.components == null && field != null && dataSource != null) {
            const md = dataSource.getMetadataFromCache();
            const mdField: MetadataField = md?.output?.[field];
            if (mdField != null) {
                compDef.components = [];
                const commonProps = { field, displayType: mdField.displayType, caption: mdField.caption, id: "cell" + field, required: mdField.required, fillRow: true };
                if (this.row.allowEdit === true) {
                    if (mdField.dataType !== (PropType.bool as string))
                        compDef.components.push({ ...commonProps, type: "textbox" });
                    else
                        compDef.components.push({ ...commonProps, type: "checkbox" });
                }
                else {
                    delete commonProps.caption;
                    compDef.components.push({ ...commonProps, type: "label" });
                }
            }
        }
    }

    override get specialParentType(): SpecialParentType {
        return SpecialParentType.TABLE_CELL;
    }

    // rowBreak isn't supported by TableCell and was removed from TableCellPropDefinitions
    // avoid calling the super which throws an error because this.parent is a Table and not a Container
    override set rowBreak(value: boolean) { }

    override get idPrefix(): string {
        return "tablecell";
    }

    override get serializationName() {
        return "cell";
    }

    override get properName(): string {
        return "Cell";
    }

    public override doBeforeComponentEnlarge(componentsToEnlarge: Component[]) {
        this.hiddenComponents = [];
        for (const component of this.components) {
            if (component.visible === true && componentsToEnlarge.includes(component) !== true) {
                this.hiddenComponents.push(component);
                component.visible = false;
            }
        }
        this.enlarged = true;
    }

    public override doAfterComponentsShrink() {
        this.enlarged = false;
        for (const hiddenComponent of this.hiddenComponents) {
            hiddenComponent.visible = true;
        }
        this.hiddenComponents = undefined;
    }

    override get enlargeScope(): Component {
        if (super.enlargeScope != null)
            return super.enlargeScope;
        super.enlargeScope = this.row;
        return super.enlargeScope;
    }

    public set enlargeScope(value: Component) {
        super.enlargeScope = value;
    }

    public override get selectableInDesigner(): boolean {
        return true;
    }

    public getEnclosingTableDataSource(): DataSource {
        return this.table.dataSource;
    }

    protected override getDesignerWidth(): number {
        return 80;
    }

    /**
     * Allows subclasses to update any pertinent state after the cell has been moved (as part of a column move/switch).
     */
    _updateAfterMove() {
    }

    defaultId() {
        // null checks for PropertiesTable :(
        if ((StringUtil.isEmptyString(this.id) || this.id === "undefined") &&
            this.table != null && this.row != null && this.col != null) {
            this.id = /*this.col.cellDef?.def?.id ||*/ this.table.id + "-" + this.row.index + "-" + this.col.index;
        }
    }
}

ComponentTypes.registerComponentType("cell", {
    constructor: TableCell.prototype.constructor
});
